import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import type { AdminSport, AdminSportsbook, SportDivision, TradedBy } from './api'
import {
  fetchSportCarriers,
  fetchSportsbookCarried,
  updateSportCarriers,
  updateSportsbookCarried,
} from './api'
import CarrierZones, { type ZoneOption, type ZoneSelection } from './CarrierZones'

// Self-contained carried-by panels, one per editor side. Own the
// fetch + edit/save lifecycle (MarketsTradedPanel pattern) so the
// trading-gated carriage edit is independent of the page-level
// admin-gated edit mode. Saves go through the full-replace RPCs,
// which write their own audit rows.

type Mode = 'view' | 'edit'

interface ShellProps {
  title: string
  canEdit: boolean
  loading: boolean
  busy: boolean
  mode: Mode
  msg: { kind: 'ok' | 'err', text: string } | null
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  children: ReactNode
}

function PanelShell({ title, canEdit, loading, busy, mode, msg, onEdit, onCancel, onSave, children }: ShellProps) {
  return (
    <div className="ad-editor-section">
      <div className="ad-markets-traded-head">
        <h3 className="b-mono">{title}</h3>
        {canEdit && !loading && (
          mode === 'view'
            ? (
                <button type="button" className="b-btn b-btn-tiny" onClick={onEdit}>
                  EDIT
                </button>
              )
            : (
                <div className="ad-markets-traded-actions">
                  <button type="button" className="b-btn-ghost b-btn-tiny" onClick={onCancel} disabled={busy}>
                    CANCEL
                  </button>
                  <button type="button" className="b-btn b-btn-primary b-btn-tiny" onClick={onSave} disabled={busy}>
                    {busy ? 'SAVING…' : 'SAVE'}
                  </button>
                </div>
              )
        )}
      </div>
      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}
      {loading ? <p className="b-mono-dim b-tiny">Loading…</p> : children}
    </div>
  )
}

// ── Sport side: which sportsbooks carry this sport ──────────────────

interface SportPanelProps {
  sportId: number
  sportsbooks: AdminSportsbook[]
  canEdit: boolean
  /** Live list of carrier sportsbook ids (deduped) — feeds the
   * AFFILIATE URLS section, which renders one row per carrier. */
  onCarrierIdsChange?: (ids: number[]) => void
}

function dedupedIds(rows: ZoneSelection[]): number[] {
  const seen = new Set<number>()
  const out: number[] = []
  for (const r of rows) {
    if (seen.has(r.id)) continue
    seen.add(r.id)
    out.push(r.id)
  }
  return out
}

export function SportCarriersPanel({ sportId, sportsbooks, canEdit, onCarrierIdsChange }: SportPanelProps) {
  const [rows, setRows] = useState<ZoneSelection[]>([])
  const [baseline, setBaseline] = useState<ZoneSelection[]>([])
  // Division-scoped carrier rows (e.g. World of Outlaws — Sprint Car)
  // can't be created from the sport side, but existing ones must render
  // and stay editable. Options for them are synthesized from the fetch.
  const [divisionOptions, setDivisionOptions] = useState<ZoneOption[]>([])
  const [mode, setMode] = useState<Mode>('view')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  function load(cancelled: () => boolean) {
    setLoading(true)
    fetchSportCarriers(sportId)
      .then((fetched) => {
        if (cancelled()) return
        const next: ZoneSelection[] = fetched.map(r => ({
          id: r.sportsbook_id,
          divisionId: r.division_id ?? null,
          tradedBy: r.traded_by,
        }))
        setRows(next)
        setBaseline(next)
        setDivisionOptions(fetched
          .filter(r => r.division_id != null)
          .map(r => ({
            id: r.sportsbook_id,
            divisionId: r.division_id,
            label: `${r.sportsbook_name ?? `#${r.sportsbook_id}`} — ${r.division_name ?? `division ${r.division_id}`}`,
            color: sportsbooks.find(b => b.id === r.sportsbook_id)?.brand_color ?? null,
          })))
        onCarrierIdsChange?.(dedupedIds(next))
      })
      .catch((e) => {
        if (!cancelled()) setMsg({ kind: 'err', text: (e as Error).message })
      })
      .finally(() => {
        if (!cancelled()) setLoading(false)
      })
  }

  useEffect(() => {
    let cancelled = false
    setMode('view')
    setMsg(null)
    setRows([])
    setBaseline([])
    setDivisionOptions([])
    load(() => cancelled)
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportId])

  function change(next: ZoneSelection[]) {
    setRows(next)
    onCarrierIdsChange?.(dedupedIds(next))
  }

  function cancel() {
    change(baseline)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateSportCarriers(sportId, rows.map(r => ({
        sportsbook_id: r.id,
        division_id: r.divisionId ?? null,
        traded_by: r.tradedBy,
      })))
      setBaseline(rows)
      setMode('view')
      const carried = dedupedIds(rows).length
      setMsg({ kind: 'ok', text: `Saved ${carried} carrier${carried === 1 ? '' : 's'}.` })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  // Whole-book options for the full catalog + synthesized division rows.
  const options: ZoneOption[] = [
    ...sportsbooks.map(b => ({ id: b.id, label: b.name, color: b.brand_color })),
    ...divisionOptions,
  ]

  return (
    <PanelShell
      title="CARRIED BY"
      canEdit={canEdit}
      loading={loading}
      busy={busy}
      mode={mode}
      msg={msg}
      onEdit={() => { setMode('edit'); setMsg(null) }}
      onCancel={cancel}
      onSave={save}
    >
      <CarrierZones
        label="SPORTSBOOKS"
        options={options}
        selected={rows}
        onChange={change}
        readOnly={mode === 'view'}
        showNotCarried={mode === 'edit'}
      />
    </PanelShell>
  )
}

// ── Sportsbook side: which sports this book carries ─────────────────

interface BookPanelProps {
  sportsbookId: number
  sports: AdminSport[]
  divisions: SportDivision[]
  canEdit: boolean
}

// Flatten sports + their divisions into one options list — same shape
// the old RelationPicker used: whole-sport row plus one row per division.
// No brand colors on this side (per Cole): sport chips use the plain ink
// fill when carried, so the zone alone carries the meaning.
function sportZoneOptions(sports: AdminSport[], divisions: SportDivision[]): ZoneOption[] {
  const divsBySport = new Map<number, SportDivision[]>()
  for (const d of divisions) {
    if (!divsBySport.has(d.sport_id)) divsBySport.set(d.sport_id, [])
    divsBySport.get(d.sport_id)!.push(d)
  }
  const out: ZoneOption[] = []
  for (const s of sports) {
    out.push({ id: s.id, label: s.name })
    for (const d of divsBySport.get(s.id) ?? []) {
      out.push({ id: s.id, divisionId: d.id, label: `${s.name} — ${d.name}` })
    }
  }
  return out
}

export function SportsbookCarriedPanel({ sportsbookId, sports, divisions, canEdit }: BookPanelProps) {
  const [rows, setRows] = useState<ZoneSelection[]>([])
  const [baseline, setBaseline] = useState<ZoneSelection[]>([])
  const [mode, setMode] = useState<Mode>('view')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setMode('view')
    setMsg(null)
    setRows([])
    setBaseline([])
    setLoading(true)
    fetchSportsbookCarried(sportsbookId)
      .then((fetched) => {
        if (cancelled) return
        const next: ZoneSelection[] = fetched.map(r => ({
          id: r.sport_id,
          divisionId: r.division_id ?? null,
          tradedBy: r.traded_by as TradedBy,
        }))
        setRows(next)
        setBaseline(next)
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [sportsbookId])

  function cancel() {
    setRows(baseline)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateSportsbookCarried(sportsbookId, rows.map(r => ({
        sport_id: r.id,
        division_id: r.divisionId ?? null,
        traded_by: r.tradedBy,
      })))
      setBaseline(rows)
      setMode('view')
      setMsg({ kind: 'ok', text: `Saved ${rows.length} sport${rows.length === 1 ? '' : 's'}.` })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <PanelShell
      title="SPORTS CARRIED"
      canEdit={canEdit}
      loading={loading}
      busy={busy}
      mode={mode}
      msg={msg}
      onEdit={() => { setMode('edit'); setMsg(null) }}
      onCancel={cancel}
      onSave={save}
    >
      <CarrierZones
        label="SPORTS"
        options={sportZoneOptions(sports, divisions)}
        selected={rows}
        onChange={setRows}
        readOnly={mode === 'view'}
        showNotCarried={mode === 'edit'}
      />
    </PanelShell>
  )
}
